import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { ListResponse, Material } from "@/api/schemas";
import type { CennikZoraditPole } from "@/api/queryKeys";
import {
  formatEur,
  formatMj,
  formatMm,
  formatPct,
  formatPocet,
  materialPrimaryId,
  menoKovania,
  toNum,
  cisloMm,
} from "@/lib/format";
import { formatKategoria } from "@/lib/kategorie";
import {
  VELKOSTI_STRANY,
  type MaterialovyTab,
  type VelkostStrany,
  type VyberRiadkov,
  type Zoradenie,
} from "@/components/cennik/cennikZalozky";
import {
  ZoraditHlavicka,
  ZoraditTlacidlo,
  ariaSortPola,
} from "@/components/cennik/ZoraditHlavicka";
import { CenaBunka } from "@/components/cennik/CenaBunka";
import { DvojickyZnak } from "@/components/cennik/DvojickyZnak";
import { ZmenaCenyZnak } from "@/components/cennik/ZmenaCenyZnak";
import { StavZnaky, vyradenaPopis } from "@/components/cennik/StavZnaky";
import { SetZnak } from "@/components/cennik/SetZnak";
import { podnazovTovaru } from "@/lib/tovar";
import { cn } from "@/lib/utils";

/** Zaškrtávanie riadkov na hromadnú úpravu (KOLO 166). */
export type TabulkaVyber = {
  vyber: VyberRiadkov;
  onPrepni: (id: string) => void;
  /** Hlavička: zaškrtni (`true`) alebo odškrtni celú zobrazenú stranu. */
  onPrepniStranu: (vybrat: boolean) => void;
};

/**
 * Checkbox, ktorý vie byť aj „čiastočne" (časť strany) — natívny
 * `indeterminate` sa dá nastaviť len cez DOM, nie atribútom.
 */
function VyberCheckbox({
  zaskrtnute,
  ciastocne = false,
  disabled = false,
  label,
  onZmena,
}: {
  zaskrtnute: boolean;
  ciastocne?: boolean;
  disabled?: boolean;
  label: string;
  onZmena: () => void;
}): JSX.Element {
  return (
    <input
      type="checkbox"
      checked={zaskrtnute}
      ref={(el) => {
        if (el) el.indeterminate = !zaskrtnute && ciastocne;
      }}
      disabled={disabled}
      onChange={onZmena}
      // Klik na riadok otvára úpravu položky — checkbox nie
      onClick={(e) => e.stopPropagation()}
      aria-label={label}
      title={label}
      className="h-4 w-4 cursor-pointer accent-primary disabled:cursor-default"
    />
  );
}

type Props = {
  tab: MaterialovyTab;
  items: Material[];
  pagination: ListResponse<Material>["pagination"] | undefined;
  isLoading: boolean;
  /**
   * Ukazujú sa riadky predošlej strany či hľadania, kým prídu nové
   * (KOLO 225) — stlmené, aby bolo vidieť, že sa načítava.
   */
  obnovuje?: boolean;
  /** Text chyby načítania, `null` = bez chyby. */
  chyba: string | null;
  onVyber: (id: string) => void;
  onStrana: (page: number) => void;
  /** Triedenie podľa stĺpca (KOLO 166), `null` = poradie servera. */
  zoradenie: Zoradenie | null;
  onZoradenie: (pole: CennikZoraditPole) => void;
  onVelkostStrany: (velkost: VelkostStrany) => void;
  /** Vráti vyradenú položku do cenníka (`je_aktivny: true`). */
  onObnovit: (m: Material) => void;
  /** Id položky, ktorej obnovenie práve beží. */
  obnovujeId: string | null;
  /** Výber riadkov na hromadnú úpravu; bez neho tabuľka nemá checkboxy. */
  vyber?: TabulkaVyber;
  /**
   * Bez práva na sekciu (KOLO 231) — ceny v bunkách sa len zobrazia
   * a vyradená položka nemá Obnoviť.
   */
  lenCitanie?: boolean;
};

/**
 * Predaj/MJ do tooltipu: vzorec a cena s nadmierou materiálu. Čísla
 * počíta server (`vyrobna_cena_per_mj`), tu sa len ukazujú.
 */
function predajTitle(m: Material): string {
  const zaklad =
    "Predaj / MJ = nákup × (1 + réžia) + práca — bez nadmiery, zisku a DPH.";
  if (toNum(m.nadmiera_pct) === 0) return zaklad;
  return (
    `${zaklad}\nS nadmierou ${formatPct(m.nadmiera_pct)}: ` +
    `${formatEur(m.predaj_mj_s_nadmierou)} (nadmiera len na nákup; ` +
    "v zákazke ju nahradí odpad z porezu)."
  );
}

/**
 * Bunka Položka (KOLO 197, variant B): hore názov so štítkami, pod ním
 * sivým kód · dekor · štruktúra · výrobca (pri kovaní kód · pravý názov
 * · výrobca). Oba riadky sa orežú, celé znenie je v tooltipe — riadok
 * tabuľky má tak vždy rovnakú výšku. Dovtedy boli ID, Dekor, Štruktúra
 * a Výrobca samostatné stĺpce a Názov bez šírky sa zalamoval na 3–4
 * riadky.
 */
function BunkaPolozky({
  material: m,
  showDekor,
  showStruktura,
  obnovuje,
  onObnovit,
}: {
  material: Material;
  showDekor: boolean;
  showStruktura: boolean;
  obnovuje: boolean;
  /** Bez neho (len na čítanie, KOLO 231) vyradená položka nemá Obnoviť. */
  onObnovit?: (m: Material) => void;
}): JSX.Element {
  const meno = menoKovania(m);
  const kod = materialPrimaryId(m);
  const dekor = showDekor ? (m.dekor_kod ?? m.propagacna_skupina) : null;
  // Tovar (KOLO 230): povrch a karta ABRY namiesto dekoru a štruktúry
  const druhy = (
    m.tovar_kod
      ? [podnazovTovaru(m)]
      : [
          // Dekor býva začiatkom kódu (F685 → F685_10mm_ST10) — dvakrát netreba
          dekor && !(kod ?? "").startsWith(dekor) ? dekor : null,
          meno.pod,
          showStruktura ? m.struktura_povrchu : null,
          m.vyrobca,
        ]
  ).filter((x): x is string => Boolean(x));
  const riadok2 = [kod, ...druhy].filter(Boolean).join(" · ");
  return (
    // Pevná výška oboch riadkov — štítok, „bez kódu" ani Obnoviť riadok
    // nezvýšia (live: riadok s „bez kódu" mal 57 px namiesto 55)
    <div className="grid min-w-0 gap-0.5">
      <div className="flex h-6 min-w-0 items-center gap-1.5">
        {m.je_set ? <SetZnak /> : null}
        {/* Pomenované kovanie: alias hlavný, pravý názov v druhom riadku (KOLO 176) */}
        <span className="truncate font-medium" title={m.nazov}>
          {meno.hlavne}
        </span>
        {/* Výpredaj a „nie je v cenníku" (KOLO 166) — výbery výpredaj
            neponúkajú, Cenník ho ukáže so štítkom */}
        <span className="flex shrink-0 items-center gap-1.5 empty:hidden [&>*]:ml-0 [&>*]:flex-nowrap">
          <StavZnaky material={m} />
          <DvojickyZnak material={m} />
        </span>
        {m.je_aktivny ? null : (
          <span className="inline-flex shrink-0 items-center gap-1">
            <span
              className="rounded-full border border-input px-1.5 py-0 text-[10px] font-medium"
              title={vyradenaPopis(m).title}
            >
              {vyradenaPopis(m).text}
            </span>
            {onObnovit ? (
              <Button
                variant="outline"
                size="sm"
                className="h-6 px-2 text-xs"
                disabled={obnovuje}
                aria-label={`Obnoviť ${m.nazov}`}
                title="Vrátiť položku do cenníka a do výberov"
                onClick={(e) => {
                  // Klik na riadok by otvoril úpravu
                  e.stopPropagation();
                  onObnovit?.(m);
                }}
              >
                Obnoviť
              </Button>
            ) : null}
          </span>
        )}
      </div>
      <div
        className="flex h-4 min-w-0 items-center gap-1.5 font-mono text-[11px] text-muted-foreground"
        title={riadok2 || undefined}
      >
        {kod ? null : (
          <span
            className="shrink-0 rounded-full border border-destructive/40 px-1.5 py-0 font-sans text-[10px] font-medium leading-3 text-destructive"
            title="Bez kódu — položka sa nedá objednať ani spárovať s cenníkom dodávateľa"
          >
            bez kódu
          </span>
        )}
        <span className="truncate">{riadok2}</span>
      </div>
    </div>
  );
}

/** Tabuľka položiek záložky Cenníka so stránkovaním (KOLO 166 z `CennikPage`). */
export function CennikTabulka({
  tab,
  items,
  pagination,
  isLoading,
  obnovuje = false,
  chyba,
  onVyber,
  onStrana,
  zoradenie,
  onZoradenie,
  onVelkostStrany,
  onObnovit,
  obnovujeId,
  vyber,
  lenCitanie = false,
}: Props): JSX.Element {
  // Stĺpce navyše podľa tabu
  const showDekor = tab !== "kovanie";
  const showDruh = tab === "materialy";
  const showRozmerHrany = tab === "hrany";
  const showMj = tab !== "hrany";
  // Štruktúra povrchu nemá pre kovanie zmysel (týka sa dekorov/hrán)
  const showStruktura = tab !== "kovanie";
  const vyrobcaLabel = tab === "hrany" ? "Kolekcia" : "Výrobca";
  const triedenie = { zoradenie, onZmena: onZoradenie };
  // „Všetky podľa filtra" = všetko zaškrtnuté, meniť sa dá len zrušením
  const podlaFiltra = vyber?.vyber.rezim === "filter";
  const jeVybrany = (id: string): boolean =>
    vyber?.vyber.rezim === "filter" ||
    (vyber?.vyber.rezim === "ids" && vyber.vyber.ids.has(id));
  const vybranychNaStrane = items.filter((m) => jeVybrany(m.id)).length;
  // Číselné stĺpce: hlavička na jeden riadok so šípkou hneď za textom,
  // bunky s rovnakým odsadením, nech čísla sedia pod hlavičkou
  const CISLO_TH = "whitespace-nowrap px-2 text-right";
  const CISLO_TD = "px-2 text-right font-mono";
  const celaStrana = items.length > 0 && vybranychNaStrane === items.length;
  // 7 spoločných: Položka, Kategória, Cena/MJ, Réžia, Práca/MJ, Predaj/MJ,
  // Dodávateľ. ID, Dekor, Štruktúra a Výrobca sú od KOLA 197 druhý riadok
  // Položky (user vybral variant B zo stránky „Vzhľad cenníka").
  const colCount =
    7 +
    (vyber ? 1 : 0) +
    (showDruh ? 1 : 0) +
    (showRozmerHrany ? 2 : 0) +
    (showMj ? 1 : 0);

  return (
    <>
      <div className="rounded-lg border bg-card">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              {vyber ? (
                <TableHead className="w-[36px] px-2">
                  <VyberCheckbox
                    zaskrtnute={celaStrana}
                    ciastocne={vybranychNaStrane > 0}
                    disabled={podlaFiltra || items.length === 0}
                    label="Vybrať všetky na tejto strane"
                    onZmena={() => vyber.onPrepniStranu(!celaStrana)}
                  />
                </TableHead>
              ) : null}
              {/* Položka = názov + pod ním kód, dekor, štruktúra, výrobca —
                  triedi sa podľa každého z nich */}
              <TableHead
                aria-sort={ariaSortPola(zoradenie, "nazov", "kod", "dekor", "struktura", "vyrobca")}
              >
                <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                  <span>Položka</span>
                  <span className="flex flex-wrap items-center gap-x-2 font-normal normal-case">
                    <ZoraditTlacidlo pole="nazov" label="Názov" {...triedenie} />
                    <ZoraditTlacidlo pole="kod" label="Kód" {...triedenie} />
                    {showDekor ? (
                      <ZoraditTlacidlo pole="dekor" label="Dekor" {...triedenie} />
                    ) : null}
                    {showStruktura ? (
                      <ZoraditTlacidlo pole="struktura" label="Štruktúra" {...triedenie} />
                    ) : null}
                    <ZoraditTlacidlo pole="vyrobca" label={vyrobcaLabel} {...triedenie} />
                  </span>
                </span>
              </TableHead>
              {showDruh ? (
                <ZoraditHlavicka
                  pole="druh"
                  label="Druh"
                  {...triedenie}
                  className="w-[76px] whitespace-nowrap"
                />
              ) : null}
              <ZoraditHlavicka
                pole="kategoria"
                label="Kategória"
                {...triedenie}
                // Hrany majú dlhé názvy („Stredná hrana (19-25mm)")
                className={tab === "hrany" ? "w-[140px]" : "w-[128px]"}
              />
              {showMj ? (
                <ZoraditHlavicka
                  pole="mj"
                  label="MJ"
                  {...triedenie}
                  className="w-[52px] whitespace-nowrap"
                />
              ) : null}
              {showRozmerHrany ? (
                <>
                  <ZoraditHlavicka
                    pole="sirka"
                    label="Šírka"
                    {...triedenie}
                    className={`w-[64px] ${CISLO_TH}`}
                  />
                  <ZoraditHlavicka
                    pole="hrubka"
                    label="Hrúbka"
                    {...triedenie}
                    className={`w-[76px] ${CISLO_TH}`}
                  />
                </>
              ) : null}
              <ZoraditHlavicka
                pole="nakup"
                label="Cena / MJ"
                {...triedenie}
                className={`w-[112px] ${CISLO_TH}`}
              />
              <ZoraditHlavicka
                pole="rezia"
                label="Réžia"
                {...triedenie}
                className={`w-[64px] ${CISLO_TH}`}
              />
              <ZoraditHlavicka
                pole="praca"
                label="Práca / MJ"
                {...triedenie}
                className={`w-[96px] ${CISLO_TH}`}
              />
              <ZoraditHlavicka
                pole="predaj"
                label="Predaj / MJ"
                {...triedenie}
                className={`w-[100px] ${CISLO_TH}`}
              />
              <ZoraditHlavicka
                pole="dodavatel"
                label="Dodávateľ"
                {...triedenie}
                className="w-[96px]"
              />
            </TableRow>
          </TableHeader>
          <TableBody
            aria-busy={obnovuje || undefined}
            className={cn(obnovuje && "opacity-60 transition-opacity")}
          >
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: colCount }).map((__, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : chyba !== null ? (
              <TableRow>
                <TableCell
                  colSpan={colCount}
                  className="py-8 text-center text-destructive"
                >
                  Chyba: {chyba}
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={colCount}
                  className="py-12 text-center text-muted-foreground"
                >
                  Žiadne materiály — skús zrušiť filtre.
                </TableCell>
              </TableRow>
            ) : (
              items.map((m) => (
                <TableRow
                  key={m.id}
                  onClick={() => onVyber(m.id)}
                  // Každý riadok dva riadky textu a rovnako vysoký — dlhý
                  // názov sa oreže, celý je v tooltipe (KOLO 197)
                  className={
                    "h-[54px] cursor-pointer [&>td]:py-1.5" +
                    (m.je_aktivny ? "" : " bg-muted/40 text-muted-foreground")
                  }
                >
                  {vyber ? (
                    <TableCell className="px-2">
                      <VyberCheckbox
                        zaskrtnute={jeVybrany(m.id)}
                        disabled={podlaFiltra}
                        label={`Vybrať ${m.nazov}`}
                        onZmena={() => vyber.onPrepni(m.id)}
                      />
                    </TableCell>
                  ) : null}
                  <TableCell className="max-w-0">
                    <BunkaPolozky
                      material={m}
                      showDekor={showDekor}
                      showStruktura={showStruktura}
                      obnovuje={obnovujeId === m.id}
                      onObnovit={lenCitanie ? undefined : onObnovit}
                    />
                  </TableCell>
                  {showDruh ? (
                    <TableCell>
                      <span
                        className={
                          "inline-block rounded-full border px-2 py-0.5 text-[10px] font-medium " +
                          (m.druh === "dlzkovy"
                            ? "border-amber-300 bg-amber-50 text-amber-800"
                            : "border-input bg-background-alt text-muted-foreground")
                        }
                      >
                        {m.druh === "dlzkovy" ? "Dĺžkový" : "Plošný"}
                      </span>
                    </TableCell>
                  ) : null}
                  <TableCell className="max-w-0 text-xs">
                    {m.kategoria ? (
                      // Dlhý názov sa oreže, celý je v tooltipe — dovtedy
                      // prepadal do susedného stĺpca (KOLO 199)
                      <div className="min-w-0" title={formatKategoria(m.kategoria)}>
                        {/* Dva riadky, keď nie je štítok — pri hranách je
                            koniec názvu („0,5 mm") to podstatné */}
                        <div
                          className={
                            m.kategoria_rucne
                              ? "truncate leading-4"
                              : "line-clamp-2 break-words leading-4"
                          }
                        >
                          {formatKategoria(m.kategoria)}
                        </div>
                        {/* Túto položku import už nepreradí — bez
                            štítku by sa nedalo zistiť, prečo sa
                            nesprává ako ostatné (KOLO 98). */}
                        {m.kategoria_rucne ? (
                          <span
                            className="inline-block rounded-full border border-primary/40 px-1.5 py-0 text-[10px] font-medium leading-4 text-primary"
                            title="Zaradenie určil človek — re-import cenníka ho už neprepíše, a s ním ani prácu a réžiu"
                          >
                            vlastná
                          </span>
                        ) : null}
                      </div>
                    ) : (
                      <span
                        className="rounded-full border border-destructive/40 px-1.5 py-0 text-[10px] font-medium text-destructive"
                        title="Bez kategórie — nemá odkiaľ vziať prácu, réžiu a nadmieru"
                      >
                        bez kategórie
                      </span>
                    )}
                  </TableCell>
                  {showMj ? (
                    <TableCell className="text-xs font-mono" title={m.mj}>
                      {formatMj(m.mj)}
                      {/* Kovanie na metre — z akej tyče sa kráti (KOLO 176) */}
                      {m.druh === "dlzkovy" && m.sekcia === "kovanie" ? (
                        <div
                          className={
                            m.x_mm
                              ? "whitespace-nowrap font-sans text-[10px] text-muted-foreground"
                              : "whitespace-nowrap font-sans text-[10px] text-amber-700 dark:text-amber-400"
                          }
                          title={
                            m.x_mm
                              ? "Dĺžka tyče — z nej počíta nárezový plán"
                              : "Dĺžku tyče cenník nepozná — doplň ju v úprave položky"
                          }
                        >
                          {m.x_mm ? `tyč ${formatMm(m.x_mm)} mm` : "bez dĺžky tyče"}
                        </div>
                      ) : null}
                    </TableCell>
                  ) : null}
                  {showRozmerHrany ? (
                    <>
                      <TableCell className={`${CISLO_TD} text-xs`}>
                        {cisloMm(m.sirka_mm)}
                      </TableCell>
                      <TableCell className={`${CISLO_TD} text-xs`}>
                        {cisloMm(m.hrubka_mm)}
                      </TableCell>
                    </>
                  ) : null}
                  {/* Nákup, réžia a práca sa menia priamo v bunke (KOLO 166) */}
                  <TableCell className={CISLO_TD}>
                    {/* Štítky pod cenou, nie vedľa nej — vedľa prekrývali
                        Réžiu (KOLO 199). Dva riadky ako Položka. */}
                    <div className="flex flex-col items-end gap-0.5">
                      <CenaBunka material={m} pole="nakup_cena" lenCitanie={lenCitanie} />
                      {m.skontroluj_cenu || toNum(m.zmena_ceny_pct) ? (
                        <div className="flex items-center gap-1 whitespace-nowrap font-sans leading-4">
                          <ZmenaCenyZnak material={m} />
                          {/* Cenník cenu nemal (Hranipex píše 42 €/m),
                              import dal medián podobných hrán (KOLO 165) */}
                          {m.skontroluj_cenu ? (
                            <span
                              className="rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0 text-[10px] font-medium text-amber-800"
                              title="Skontroluj cenu — cenník dodávateľa ju nemá, appka dala odhad z podobných položiek. Oprav nákupnú cenu v bunke alebo v detaile; štítok potom zhasne a import ju už neprepíše."
                            >
                              odhad
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className={`${CISLO_TD} text-xs`}>
                    <CenaBunka material={m} pole="rezia_pct" lenCitanie={lenCitanie} />
                  </TableCell>
                  <TableCell className={CISLO_TD}>
                    <CenaBunka material={m} pole="praca_eur_per_mj" lenCitanie={lenCitanie} />
                  </TableCell>
                  <TableCell
                    className={`${CISLO_TD} font-medium`}
                    title={predajTitle(m)}
                  >
                    {formatEur(m.predaj_mj)}
                  </TableCell>
                  <TableCell
                    className="truncate text-xs"
                    title={m.dodavatel_nazov ?? m.dodavatel ?? undefined}
                  >
                    {m.dodavatel_nazov ?? m.dodavatel ?? "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Od 51 položiek — aj jedna strana po 100 musí ísť vrátiť na 50 */}
      {pagination && pagination.total > VELKOSTI_STRANY[0] ? (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
          <span>
            Zobrazené {formatPocet((pagination.page - 1) * pagination.per_page + 1)}–
            {formatPocet(
              Math.min(pagination.page * pagination.per_page, pagination.total),
            )}{" "}
            zo {formatPocet(pagination.total)}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs">Na stránku</span>
            <Select
              value={String(pagination.per_page)}
              onValueChange={(v) => onVelkostStrany(Number(v) as VelkostStrany)}
            >
              <SelectTrigger className="h-8 w-[80px]" aria-label="Položiek na stránku">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VELKOSTI_STRANY.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => onStrana(Math.max(1, pagination.page - 1))}
            >
              ‹ Predchádzajúca
            </Button>
            <span className="self-center font-mono">
              {pagination.page} / {pagination.total_pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() => onStrana(pagination.page + 1)}
            >
              Ďalšia ›
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}
